// AiDiagramQuickAction — Dropdown-knapp med prefilled prompts för multi-
// sheet-diagram-authoring. Exponeras bredvid input-fältet i AiAssistantDialog.
// Click → fyller input med template + (valfritt) skickar direkt.

import { memo, useState } from 'react';
import { Sparkles, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

export interface DiagramTemplate {
  id: string;
  label: string;
  /** Natural-language prompt som fyller input-fältet. */
  prompt: string;
  description?: string;
  /**
   * AB1: True = admin-only. Module-graph + ERD exponerar applikationens
   * interna kod respektive DB-struktur — visas ENDAST för sysadmins.
   */
  requiresAdmin?: boolean;
}

const TEMPLATES: DiagramTemplate[] = [
  {
    id: 'architecture',
    label: 'Kartlägg arkitektur',
    prompt: 'Kartlägg hela appens arkitektur som ett multi-sheet-diagram — en sheet per stor domän (UI, persistence, AI, admin). Inkludera cross-sheet-länkar där data flödar mellan domänerna.',
    description: '5-6 sheets, 30-50 blocks',
  },
  {
    id: 'module-graph',
    label: 'Rita moduldiagram',
    prompt: 'Rita ett moduldiagram av kodbasen — en sheet per top-level-folder, ett block per fil, imports som connectors. Cross-sheet-länkar för imports över moduler.',
    description: '4-6 sheets, 1 block/fil · admin-only',
    requiresAdmin: true,
  },
  {
    id: 'erd',
    label: 'Skapa ERD',
    prompt: 'Skapa en ERD (entity-relationship-diagram) för databas-schemat — en sheet per schema, ett block per tabell, foreign keys som connectors.',
    description: '1-3 sheets, 1 block/tabell · admin-only',
    requiresAdmin: true,
  },
  {
    id: 'pipeline',
    label: 'Deploy-pipeline',
    prompt: 'Rita ut deploy-pipelinen som linjär kedja — från git-push via CI till produktion. Använd condition-block för gates/checks.',
    description: '1 sheet, ~10 blocks',
  },
  {
    id: 'state-machine',
    label: 'State machine',
    prompt: 'Skapa en state-machine — condition-blocks som decision-diamanter, general-blocks som states. Transitions som labeled connectors.',
    description: '1 sheet, 5-15 states',
  },
];

interface Props {
  onSelectTemplate: (template: DiagramTemplate) => void;
  disabled?: boolean;
  /** AB1: när false/undefined filtreras admin-only-templates bort. */
  isAdmin?: boolean;
}

export const AiDiagramQuickAction = memo(function AiDiagramQuickAction({
  onSelectTemplate,
  disabled,
  isAdmin,
}: Props) {
  const [open, setOpen] = useState(false);
  const visibleTemplates = TEMPLATES.filter((t) => !t.requiresAdmin || isAdmin === true);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex-shrink-0 gap-1.5 h-9"
          disabled={disabled}
          title="Build diagram"
          data-testid="ai-diagram-quick-action"
          data-guide="ai-diagram-quick-action"
        >
          <Sparkles className="w-3.5 h-3.5" />
          <span className="hidden sm:inline text-xs">Diagram</span>
          <ChevronDown className="w-3 h-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Bygg multi-sheet-diagram
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {visibleTemplates.map((t) => (
          <DropdownMenuItem
            key={t.id}
            onClick={() => { onSelectTemplate(t); setOpen(false); }}
            className="flex flex-col items-start gap-0.5 cursor-pointer"
          >
            <span className="text-sm font-medium">{t.label}</span>
            {t.description && (
              <span className="text-[10px] text-muted-foreground">{t.description}</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

export { TEMPLATES as DIAGRAM_TEMPLATES };
